import Sidebar from "../layouts/sidebar";
import Header from "../layouts/header";
import Layout from "../layouts/layout";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import candidateActions from "../redux/candidate/actions";
import commonActions from "../redux/common/actions";
import { useSelector } from "react-redux";
import TablePagination from '@mui/material/TablePagination';
import CandidateDumpTable from "../common/CandidateDumpTable";
import CandidateDumpHistory from "../common/CandidateDumpHistory";
import JSZip from 'jszip';
import axios from "axios";
import Cookies from "js-cookie";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    TextField,
    Typography,
} from "@mui/material";
import { MdSync, MdStop, MdOutlineEmail } from "react-icons/md";
import {
    API_URL,
    FORM_ASYNC_API_URL,
    DUMP_BUCKET_NAME,
    DUMP_GCP_PROJECT_ID,
    DUMP_BASE_PREFIX,
} from "../utils/constants";

// The bucket sync is a long running gsutil rsync on the server, so we poll its
// status endpoint instead of holding the request open.
const SYNC_POLL_INTERVAL = 30000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_DUMP_EMAIL_RECIPIENTS = 10;

const parseEmails = (value) => [...new Set(
    String(value || "").split(",").map((email) => email.trim().toLowerCase()).filter(Boolean)
)];


const CandidateDump = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);


    const [syncStatus, setSyncStatus] = useState(null);
    const [syncStarting, setSyncStarting] = useState(false);
    const [syncStopping, setSyncStopping] = useState(false);
    const [dumpChoice, setDumpChoice] = useState({ open: false, row: null, isRegenerate: null });
    const syncPollingRef = useRef(null);
    const [emailInput, setEmailInput] = useState("");
    const [emailError, setEmailError] = useState("");
    const [emailSending, setEmailSending] = useState(false);
    // { [courseid]: [emails] } for each course's latest completed dump.
    const [emailAccess, setEmailAccess] = useState({});
    // { courseid, requestid } when "Add email id" is used in Dump history.
    const [historyEmailTarget, setHistoryEmailTarget] = useState(null);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

    const dispatch = useDispatch()
    const { generatingRowIds, generatedRows, courseNameForDump, pendingDump, dumpEmailPromptCourseId } = useSelector((state) => state.candidateReducer);

    const getToken = () => Cookies.get("mhet_mst_token");

    const showAlert = (status, message) => {
        dispatch({ type: commonActions.SET_ALERT, payload: { status, show: true, message } });
        setTimeout(() => {
            dispatch({ type: commonActions.SET_ALERT, payload: { status, show: false, message: null } });
        }, 3000);
    };

    const fetchSyncStatus = async () => {
        try {
            const { data } = await axios.get(`${FORM_ASYNC_API_URL}/getBucketMigrationStatus`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            // The async-api auth middleware answers with HTTP 200 + statusCode 400,
            // so trust the payload only when it actually carries a migration status.
            setSyncStatus(data?.status ? data : null);
        } catch (err) {
            // 404 simply means no migration has ever been started on this server.
            setSyncStatus(null);
        }
    };

    const handleSync = async () => {
        setSyncStarting(true);
        try {
            const { data } = await axios.post(`${FORM_ASYNC_API_URL}/startBucketMigration`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (data?.statusCode && data.statusCode >= 400) {
                showAlert("failed", data?.message || "Failed to start bucket sync");
            } else {
                showAlert("success", data?.message || "Bucket sync started");
            }
        } catch (err) {
            showAlert("failed", err?.response?.data?.message || "Failed to start bucket sync");
        } finally {
            setSyncStarting(false);
            fetchSyncStatus();
        }
    };

    const handleStopSync = async () => {
        setSyncStopping(true);
        try {
            const { data } = await axios.post(`${FORM_ASYNC_API_URL}/stopBucketMigration`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (data?.statusCode && data.statusCode >= 400) {
                showAlert("failed", data?.message || "Failed to stop bucket sync");
            } else {
                showAlert("success", data?.message || "Bucket sync stopped");
            }
        } catch (err) {
            showAlert("failed", err?.response?.data?.message || "Failed to stop bucket sync");
        } finally {
            setSyncStopping(false);
            fetchSyncStatus();
        }
    };

    const fetchEmailAccess = async () => {
        try {
            const { data } = await axios.get(`${FORM_ASYNC_API_URL}/dumpEmailAccess`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (data?.statusCode === 200) setEmailAccess(data?.result || {});
        } catch (err) {
            // The column just shows "-" if this cannot be loaded.
        }
    };

    // The email dialog serves both the latest dump (table above) and a specific
    // request picked in Dump history.
    const emailTargetCourseId = historyEmailTarget?.courseid ?? dumpEmailPromptCourseId;
    const emailTargetRequestId = historyEmailTarget?.requestid ?? null;

    const closeEmailPrompt = () => {
        setEmailInput("");
        setEmailError("");
        setHistoryEmailTarget(null);
        dispatch({ type: candidateActions.CLEAR_DUMP_EMAIL_PROMPT });
    };

    // After a dump completes, email its link and secret key to the ids entered.
    const handleSendDumpEmail = async () => {
        const emails = parseEmails(emailInput);
        if (!emails.length) {
            setEmailError("Enter at least one email id.");
            return;
        }
        if (emails.length > MAX_DUMP_EMAIL_RECIPIENTS) {
            setEmailError(`At most ${MAX_DUMP_EMAIL_RECIPIENTS} email ids.`);
            return;
        }
        const invalid = emails.find((email) => !EMAIL_RE.test(email));
        if (invalid) {
            setEmailError(`"${invalid}" is not a valid email id.`);
            return;
        }

        setEmailSending(true);
        try {
            const { data } = await axios.post(
                `${FORM_ASYNC_API_URL}/sendDumpEmail/${emailTargetCourseId}`,
                emailTargetRequestId ? { recipients: emails, requestid: emailTargetRequestId } : { recipients: emails },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            // The async-api auth middleware answers with HTTP 200 + statusCode 400.
            if (data?.statusCode && data.statusCode >= 400) {
                setEmailError(data?.message || "Failed to send email");
                return;
            }
            showAlert("success", data?.message || "Email sent");
            closeEmailPrompt();
            fetchEmailAccess();
            setHistoryRefreshKey((key) => key + 1);
        } catch (err) {
            setEmailError(err?.response?.data?.message || "Failed to send email");
        } finally {
            setEmailSending(false);
        }
    };

    const emailPromptCourseName = (courseNameForDump || [])
        .find((row) => String(row?.courseid) === String(emailTargetCourseId))?.coursename;

    console.log(courseNameForDump,"courseNameForDump")

    const handleGenerateFunc = (row) => {
        setDumpChoice({ open: true, row, isRegenerate: null });
    }

    const handleRegenrateFunc = (row, isRegenerate) => {
        setDumpChoice({ open: true, row, isRegenerate });
    }

    const closeDumpChoice = () => setDumpChoice({ open: false, row: null, isRegenerate: null });

    // isExcelDownload true -> spreadsheet only, false -> also pulls photos and signatures.
    const startDump = (isExcelDownload) => {
        const { row, isRegenerate } = dumpChoice;
        closeDumpChoice();
        dispatch({
            type: candidateActions.GENERATE_DUMP_REQUEST, payload: {
                rowId: row?.courseid,
                isRegenerate: isRegenerate,
                isExcelDownload: isExcelDownload,
            }
        });
    }

    let applicationStatusColumns = [
        {
            name: "Course Name",
            dataIndex: "coursename",
        },
        {
            name: "Total Candidates",
            dataIndex: "candidatecount",
        },
        {
            name: "Action",
            dataIndex: "action",
        },
        {
            name: "Email ID Access",
            dataIndex: "emailaccess",
            render: (_, row) => {
                // A key here means the course has a completed dump that can be emailed.
                const hasCompletedDump = Object.prototype.hasOwnProperty.call(emailAccess, row?.courseid);
                const emails = emailAccess[row?.courseid] || [];
                if (!hasCompletedDump) return "-";
                return (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, fontSize: 13, wordBreak: "break-all" }}>
                        {emails.map((email) => <span key={email}>{email}</span>)}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MdOutlineEmail />}
                            onClick={() => dispatch({ type: candidateActions.OPEN_DUMP_EMAIL_PROMPT, payload: row?.courseid })}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                        >
                            Add email id
                        </Button>
                    </Box>
                );
            },
        },
    ];



    // const applicationStatusData = [
    //     {
    //         coursename: "Advanced Robotics Engineering",
    //         totalcandidates: 800076,
    //         action: "",
    //         id: 1
    //     },
    //     {
    //         coursename: "Digital Marketing Strategies",
    //         totalcandidates: 858000,
    //         action: "",
    //         id: 2
    //     },
    //     {
    //         coursename: "Data Science Fundamentals",
    //         totalcandidates: 900900,
    //         action: "",
    //         id: 3
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 4
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 5
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 6
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 7
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 8
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 9
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 10
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 11
    //     },
    //     {
    //         coursename: "Creative Writing Workshop",
    //         totalcandidates: 950578,
    //         action: "",
    //         id: 12
    //     },
    // ];


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // No signed URL: open the course's folder in the GCP console. The user signs
    // in to GCP there and downloads the zip they want (excel or withphotosignature).
    const handleDownloadDump = (row) => {
        const courseid = row?.courseid;
        if (!courseid) return;

        const folderPath = `${DUMP_BASE_PREFIX}/course_${courseid}`;
        const consoleUrl =
            `https://console.cloud.google.com/storage/browser/${DUMP_BUCKET_NAME}/${folderPath}` +
            `?project=${DUMP_GCP_PROJECT_ID}`;

        window.open(consoleUrl, "_blank", "noopener,noreferrer");
    };

      const data = courseNameForDump || [];
    const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);



    // const paginatedData = applicationStatusData.slice(
    //     page * rowsPerPage,
    //     page * rowsPerPage + rowsPerPage
    // );




    useEffect(() => {
        dispatch({
            type: candidateActions?.GET_COURSE_NAME_FOR_DUMP,
            payload: {},
        });
        fetchEmailAccess();
    }, []);

    // A dump that just completed (or a re-generated one) changes who has access.
    useEffect(() => {
        if (dumpEmailPromptCourseId) {
            fetchEmailAccess();
            setHistoryRefreshKey((key) => key + 1);
        }
    }, [dumpEmailPromptCourseId]);

    useEffect(() => {
        fetchSyncStatus();
        syncPollingRef.current = setInterval(fetchSyncStatus, SYNC_POLL_INTERVAL);
        return () => {
            if (syncPollingRef.current) clearInterval(syncPollingRef.current);
        };
    }, []);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(data.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(0);
        }
    }, [data.length, page, rowsPerPage]);

    const isSyncRunning = syncStatus?.status === "running";
    const syncPercentage = Number(syncStatus?.percentage) || 0;

    return (
        <>
            <Sidebar />
            <Header />
            <Layout>
                <div style={{ width: "100%", backgroundColor: "#fff", padding: 10, minHeight: "100%" }}>
                    <div style={{ width: "100%", backgroundColor: "#e8e8e8", padding: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                        <p style={{ fontWeight: "500", color: "#212121", fontSize: 18, lineHeight: "100%", marginLeft: 5 }}>Candidate dump</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSync}
                                disabled={syncStarting || isSyncRunning}
                                startIcon={syncStarting || isSyncRunning ? <CircularProgress size={16} color="inherit" /> : <MdSync />}
                                sx={{ textTransform: "none", minWidth: "140px", whiteSpace: "nowrap" }}
                            >
                                {isSyncRunning ? "Syncing..." : "Sync"}
                            </Button>

                            {isSyncRunning && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={handleStopSync}
                                    disabled={syncStopping}
                                    startIcon={syncStopping ? <CircularProgress size={16} color="inherit" /> : <MdStop />}
                                    sx={{ textTransform: "none", minWidth: "110px", whiteSpace: "nowrap" }}
                                >
                                    {syncStopping ? "Stopping..." : "Stop"}
                                </Button>
                            )}
                        </Box>
                    </div>

                    {syncStatus && (
                        <Box sx={{ px: 1, pt: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <Typography sx={{ fontSize: 14, color: "#212121" }}>
                                    Bucket sync: {syncStatus?.status}
                                    {syncStatus?.filesCopied && syncStatus?.filesTotal
                                        ? ` — ${syncStatus.filesCopied}/${syncStatus.filesTotal} files`
                                        : syncStatus?.filesCopied
                                            ? ` — ${syncStatus.filesCopied} files`
                                            : ""}
                                    {syncStatus?.bytesCopied && syncStatus?.bytesTotal
                                        ? ` (${syncStatus.bytesCopied}/${syncStatus.bytesTotal})`
                                        : ""}
                                    {syncStatus?.eta ? ` — ETA ${syncStatus.eta}` : ""}
                                </Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0F4F96" }}>
                                    {syncPercentage}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={syncPercentage}
                                color={syncStatus?.status === "failed" ? "error" : "primary"}
                                sx={{ height: 8, borderRadius: 4, mt: 1 }}
                            />
                            {syncStatus?.errormessage && (
                                <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
                                    {syncStatus.errormessage}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Table below */}
                    <CandidateDumpTable
                        tableData={paginatedData}
                        columnData={applicationStatusColumns}
                        handleGenerateFunc={handleGenerateFunc}
                        generatingRowIds={generatingRowIds}
                        generatedRows={generatedRows}
                        pendingDump={pendingDump}
                        handleDownloadDump={handleDownloadDump}
                        handleRegenrateFunc={handleRegenrateFunc}
                    />

                    {/* Directly under the course table, so it no longer floats over Dump history */}
                    <TablePagination
                        component="div"
                        count={data.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10]}
                        sx={{ pr: 2 }}
                    />

                    {/* Every dump request of one course, to give email ids access to any of them */}
                    <CandidateDumpHistory
                        courses={courseNameForDump || []}
                        refreshKey={historyRefreshKey}
                        onAddEmail={(courseid, requestid) => setHistoryEmailTarget({ courseid, requestid })}
                    />
                </div>

                <Dialog open={dumpChoice.open} onClose={closeDumpChoice} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontSize: 18, fontWeight: 500 }}>
                        Generate candidate dump
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontSize: 14 }}>
                            What should the dump for{" "}
                            <b>{dumpChoice.row?.coursename || "this course"}</b> contain?
                            Including photos and signatures takes considerably longer.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={closeDumpChoice} sx={{ textTransform: "none" }}>
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => startDump(true)}
                            sx={{ textTransform: "none" }}
                        >
                            Excel only
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => startDump(false)}
                            sx={{ textTransform: "none" }}
                        >
                            With photo &amp; signature
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(emailTargetCourseId)}
                    onClose={emailSending ? undefined : closeEmailPrompt}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ fontSize: 18, fontWeight: 500 }}>
                        Send candidate dump by email
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
                            {emailTargetRequestId ? (
                                <>Dump request <b>#{emailTargetRequestId}</b> for <b>{emailPromptCourseName || "this course"}</b>.</>
                            ) : (
                                <>The dump for <b>{emailPromptCourseName || "this course"}</b> is ready.</>
                            )}{" "}
                            Enter the email ids to send the download link and secret key to, separated by commas.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            label="Email ids"
                            placeholder="name1@example.com, name2@example.com"
                            value={emailInput}
                            onChange={(e) => {
                                setEmailInput(e.target.value);
                                if (emailError) setEmailError("");
                            }}
                            error={Boolean(emailError)}
                            helperText={emailError || `Comma separated, up to ${MAX_DUMP_EMAIL_RECIPIENTS} email ids.`}
                            disabled={emailSending}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={closeEmailPrompt} disabled={emailSending} sx={{ textTransform: "none" }}>
                            Skip
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSendDumpEmail}
                            disabled={emailSending}
                            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ textTransform: "none" }}
                        >
                            {emailSending ? "Sending..." : "Send"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Layout>
        </>
    )
}

export default CandidateDump;